import React from 'react';
import { Plus, ArrowRight, FolderOpen, CloudOff, RotateCw, Loader2 } from 'lucide-react';
import type { Workflow, Project } from '../../../types';
import { CanvasCard } from './CanvasCard';
import { useCanvasGallery, CanvasTab } from '../../../hooks/home/useCanvasGallery';
import { useProjectStore } from '../../../stores/projectStore';

interface CanvasGalleryProps {
  workflows: Workflow[];
  projects?: Project[];
  onSelectWorkflow: (id: string) => void;
  onCreateProject: () => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, newTitle: string) => void;
  onViewAll?: () => void;
  limit?: number;
  showHeader?: boolean;
}

const TABS: { key: CanvasTab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'personal', label: '个人' },
  // 设计稿 1:9 第三枚 chip 文案为「协助」（node 1:48）
  { key: 'collaboration', label: '协助' },
];

export const CanvasGallery: React.FC<CanvasGalleryProps> = ({
  workflows,
  projects,
  onSelectWorkflow,
  onCreateProject,
  onDeleteWorkflow,
  onRenameWorkflow,
  onViewAll,
  limit,
  showHeader = true,
}) => {
  // 直接订阅列表加载状态：区分「真的没有画布」与「加载失败」，失败时展示错误 + 重试
  const loadError = useProjectStore((s) => s.loadError);
  const isListLoading = useProjectStore((s) => s.isLoading);
  const retryLoadProjects = useProjectStore((s) => s.loadProjects);

  const {
    searchQuery: _searchQuery,
    setSearchQuery: _setSearchQuery,
    activeTab,
    setActiveTab,
    filteredProjects,
    editingId,
    editingTitle,
    setEditingTitle,
    startRename,
    saveRename,
    cancelRename,
    deleteProject,
  } = useCanvasGallery({
    workflows,
    projects,
    onSelectWorkflow,
    onDeleteWorkflow,
    onRenameWorkflow,
  });

  const displayProjects = typeof limit === 'number' && limit > 0 ? filteredProjects.slice(0, limit) : filteredProjects;

  return (
    /* 设计稿 141:43：工作组与对话框同列 1200 */
    <div className="w-full max-w-[1200px] mx-auto">
      {showHeader && (
        /* 设计稿 chips 底 789 → 卡片顶 805（x=760 精确扫描）：间距 16px */
        <div className="flex items-center justify-between mb-4" style={{ height: '32px' }}>
          <div className="flex items-center">
            {TABS.map((tab, index) => {
              const isActive = activeTab === tab.key;
              const marginLeft = index === 0 ? 0 : index === 1 ? 8 : 8;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="text-white transition-colors"
                  style={{
                    fontSize: '14px',
                    fontFamily: '"PingFang SC", sans-serif',
                    lineHeight: '14px',
                    // 固定宽高与 padding，避免切换激活态时按钮位移
                    width: '60px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#2D2D2D' : 'transparent',
                    marginLeft: `${marginLeft}px`,
                    padding: '0 0 0 16px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {onViewAll && (
            <button
              onClick={onViewAll}
              className="flex items-center gap-1 text-white transition-opacity hover:opacity-80"
              style={{ fontSize: '14px', fontFamily: '"PingFang SC", sans-serif', lineHeight: '14px' }}
            >
              所有画布
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* 设计稿 141:43：4 列卡片 294×160，间隙 8px（294+8=302） */}
      <div className="grid grid-cols-4 gap-2 canvas-gallery-grid">
        <button
          onClick={onCreateProject}
          className="canvas-card group relative border-dashed transition-all duration-300 cursor-pointer overflow-hidden text-left flex flex-col items-center justify-center"
          style={{
            backgroundColor: '#2D2D2D',
            borderColor: '#4D4D4D',
            borderRadius: '16px',
            height: '160px',
            borderWidth: '1px',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-zinc-700/60 flex items-center justify-center group-hover:bg-zinc-600/80 transition-colors mb-2">
            <Plus size={20} className="text-zinc-300" />
          </div>
          <span className="text-sm text-zinc-400 group-hover:text-zinc-200">新建画布</span>
        </button>

        {displayProjects.length === 0 && loadError ? (
          <div
            className="canvas-card col-span-3 flex items-center gap-3 px-4 border"
            style={{ backgroundColor: '#2A2222', borderColor: '#5A3636', borderRadius: '12px', height: '160px' }}
          >
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
              <CloudOff size={18} className="text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-200">项目列表加载失败</p>
              <p className="text-xs text-zinc-500 mt-0.5 truncate">{loadError}</p>
            </div>
            <button
              onClick={() => void retryLoadProjects()}
              disabled={isListLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-zinc-200 transition-colors hover:bg-zinc-700/60 disabled:opacity-50 flex-shrink-0"
              style={{ backgroundColor: '#3A3A3A' }}
            >
              <RotateCw size={13} className={isListLoading ? 'animate-spin' : ''} />
              {isListLoading ? '重试中...' : '重新加载'}
            </button>
          </div>
        ) : displayProjects.length === 0 && isListLoading ? (
          <div
            className="canvas-card col-span-3 flex items-center gap-3 px-4 border"
            style={{ backgroundColor: '#222222', borderColor: '#363636', borderRadius: '12px', height: '160px' }}
          >
            <div className="w-10 h-10 rounded-xl bg-zinc-800/60 flex items-center justify-center flex-shrink-0">
              <Loader2 size={18} className="text-zinc-400 animate-spin" />
            </div>
            <div>
              <p className="text-sm text-zinc-300">正在加载画布...</p>
              <p className="text-xs text-zinc-500 mt-0.5">正在从服务器同步你的项目</p>
            </div>
          </div>
        ) : displayProjects.length === 0 ? (
          <div
            className="canvas-card canvas-empty-state col-span-3 flex items-center gap-3 px-4 border"
            style={{ backgroundColor: '#222222', borderColor: '#363636', borderRadius: '12px', height: '160px' }}
          >
            <div className="w-10 h-10 rounded-xl bg-zinc-800/60 flex items-center justify-center flex-shrink-0">
              <FolderOpen size={18} className="text-zinc-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-300">还没有画布</p>
              <p className="text-xs text-zinc-500 mt-0.5">输入上方指令开始创作，或点击「新建画布」</p>
            </div>
          </div>
        ) : (
          displayProjects.map((project) => {
            const workflow = workflows.find((w) => w.id === project.id);
            return (
              <CanvasCard
                key={project.id}
                project={project}
                workflow={workflow}
                isEditing={editingId === project.id}
                editingTitle={editingTitle}
                onOpen={onSelectWorkflow}
                onRenameStart={startRename}
                onRenameChange={setEditingTitle}
                onRenameSave={saveRename}
                onRenameCancel={cancelRename}
                onDelete={deleteProject}
              />
            );
          })
        )}
      </div>
    </div>
  );
};
